import { refreshAccessToken } from "../services/api";

export const getAccessToken = async (): Promise<string | null> => {
    const accessToken = localStorage.getItem("accessToken");

    // Check for external form_entry token if no regular user token
    if (!accessToken) {
        const externalToken = localStorage.getItem("external_form_entry_token");
        if (externalToken) {
            return externalToken;
        }
        return null;
    }

    return accessToken;
};

export const createAuthHeaders = async (): Promise<HeadersInit> => {
    const accessToken = await getAccessToken();
    return {
        "Content-Type": "application/json",
        ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
    };
};

export const createPDFAuthHeaders = async (): Promise<HeadersInit> => {
    const accessToken = await getAccessToken();
    return {
        "Content-Type": "application/pdf",
        ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
    };
};

export const clearTokens = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
    localStorage.removeItem("external_form_entry_token");
    localStorage.removeItem("external_link_id");

    // Clear cookies
    document.cookie = "user=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;";
    document.cookie =
        "accessToken=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;";
};

export const postWithAuth = async (url: string, body?: object) => {
    const headers = await createAuthHeaders();
    const request_obj: any = {
        method: "POST",
        headers,
    };
    if (body) {
        request_obj["body"] = JSON.stringify(body);
    }


    const response = await fetch(url, request_obj);


    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${response.statusText}. Response: ${errorText}`);
    }

    const responseText = await response.text();

    try {
        return JSON.parse(responseText);
    } catch (parseError) {
        throw new Error(`Invalid JSON response: ${responseText}`);
    }
};

export const putWithAuth = async (url: string, body?: object) => {
    const headers = await createAuthHeaders();
    const request_obj: any = {
        method: "PUT",
        headers,
    };
    if (body) {
        request_obj["body"] = JSON.stringify(body);
    }

    const response = await fetch(url, request_obj);

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${response.statusText}. Response: ${errorText}`);
    }

    const responseText = await response.text();

    try {
        return JSON.parse(responseText);
    } catch (parseError) {
        throw new Error(`Invalid JSON response: ${responseText}`);
    }
};

export const getWithAuth = async (url: string) => {
    const headers = await createAuthHeaders();
    const response = await fetch(url, {
        method: "GET",
        headers,
    });
    return response.json();
};

export const getHtml = async (url: string, flowData?: any) => {
    const headers = await createAuthHeaders();
    const response = await fetch(url, {
        method: "GET",
        headers,
    });
    return response.text();
};

export const getPDF = async (
    url: string,
    filename = "document.pdf",
    flowData?: any
) => {
    const headers = await createPDFAuthHeaders();
    const response = await fetch(url, {
        method: "GET",
        headers,
    });

    if (!response.ok) throw new Error("Failed to fetch PDF");

    const blob = await response.blob();
    const pdfUrl = URL.createObjectURL(blob);

    // ✅ Force download
    const a = document.createElement("a");
    a.href = pdfUrl;
    a.download = filename; // set the file name
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    // cleanup
    URL.revokeObjectURL(pdfUrl);
};

export const patchWithAuth = async (url: string, body?: object) => {
    const headers = await createAuthHeaders();
    const request_obj: any = {
        method: "PATCH",
        headers,
    };
    if (body) {
        request_obj["body"] = JSON.stringify(body);
    }
    const response = await fetch(url, request_obj);
    return response.json();
};

export const deleteWithAuth = async (url: string, body?: object) => {
    const headers = await createAuthHeaders();
    const request_obj: any = {
        method: "DELETE",
        headers,
    };
    if (body) {
        request_obj["body"] = JSON.stringify(body);
    }
    const response = await fetch(url, request_obj);
    return response.json();
};
